import React from 'react';

const AiConfiguration = ({ config, onChange, isLoading, isConnected }) => {
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    onChange(name, type === 'checkbox' ? checked : (type === 'range' || type === 'number' ? Number(value) : value));
  };

  const sliderBackground = (value, min, max) => {
    const percentage = ((value - min) / (max - min)) * 100;
    return `linear-gradient(to right, #9e3eff ${percentage}%, #2b253a ${percentage}%)`;
  };

  return (
    <div className={`bg-dark-600 rounded-2xl p-6 h-full flex flex-col transform-gpu hover:scale-[1.005] transition-medium border border-dark-400 hover:border-accent-purple/50 hover:shadow-glow-purple group relative overflow-hidden ${isLoading || !isConnected ? 'opacity-70 cursor-not-allowed' : ''}`}>
      <div className="absolute -left-16 -top-16 w-48 h-48 bg-accent-purple opacity-[0.03] rounded-full blur-3xl group-hover:opacity-[0.06] transition-opacity duration-300"></div>
      <h3 className="font-bold text-lg mb-6 flex items-center relative z-10">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-purple/80 to-accent-pink/80 flex items-center justify-center mr-3 shadow-md group-hover:scale-110 transition-transform duration-300">
          <i className="fas fa-sliders-h text-white"></i>
        </div>
        AI Configuration
      </h3>

      <div className={`space-y-5 flex-1 relative z-10 ${isLoading || !isConnected ? 'pointer-events-none' : ''}`}>
        <div>
          <label htmlFor="analysisDepth" className="block text-sm font-medium text-gray-300 mb-1 group-hover:text-white transition-colors">AI Analysis Depth ({config.analysisDepth})</label>
          <input
            type="range"
            id="analysisDepth"
            name="analysisDepth"
            min="1"
            max="5"
            value={config.analysisDepth}
            onChange={handleInputChange}
            disabled={isLoading || !isConnected}
            className="w-full h-3 rounded-lg appearance-none cursor-pointer focus:outline-none range-slider-purple"
            style={{ background: sliderBackground(config.analysisDepth, 1, 5) }}
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1 px-1">
            <span>Basic</span>
            <span>Standard</span>
            <span>Deep</span>
          </div>
        </div>

        <div>
          <label htmlFor="analysisFocus" className="block text-sm font-medium text-gray-300 mb-1 group-hover:text-white transition-colors">Analysis Focus</label>
          <select
            id="analysisFocus"
            name="analysisFocus"
            value={config.analysisFocus}
            onChange={handleInputChange}
            disabled={isLoading || !isConnected}
            className="w-full bg-dark-500 custom-input rounded-lg py-2.5 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-2 focus:ring-accent-purple/50 hover:border-dark-300 transition-all"
          >
            <option value="balanced">Balanced Analysis</option>
            <option value="plugins">Plugin Vulnerabilities</option>
            <option value="config">Configuration Issues</option>
            <option value="network">Network Security</option>
            <option value="auth">Authentication Systems</option>
          </select>
        </div>

        <div>
          <label htmlFor="aiModel" className="block text-sm font-medium text-gray-300 mb-1 group-hover:text-white transition-colors">AI Model</label>
          <select
            id="aiModel"
            name="aiModel"
            value={config.aiModel}
            onChange={handleInputChange}
            disabled={isLoading || !isConnected}
            className="w-full bg-dark-500 custom-input rounded-lg py-2.5 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-2 focus:ring-accent-purple/50 hover:border-dark-300 transition-all"
          >
            <option value="zerodai-v3.5">ZeroDaiMethod v3.5 (Advanced)</option>
            <option value="zerodai-v3.2">ZeroDaiMethod v3.2 (Standard)</option>
            <option value="legacy-v2.8">Legacy Model v2.8</option>
          </select>
        </div>
        
        <div>
          <label htmlFor="connectionType" className="block text-sm font-medium text-gray-300 mb-1 group-hover:text-white transition-colors">Connection Type</label>
          <select
            id="connectionType"
            name="connectionType"
            value={config.connectionType}
            onChange={handleInputChange}
            disabled={isLoading || !isConnected}
            className="w-full bg-dark-500 custom-input rounded-lg py-2.5 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-2 focus:ring-accent-purple/50 hover:border-dark-300 transition-all"
          >
            <option value="direct">Direct Connection</option>
            <option value="proxy">Through Proxy Chain</option>
            <option value="vpn">VPN Connection</option>
          </select>
        </div>
         <div className="flex items-center justify-between pt-2">
            <label htmlFor="stealthMode" className="text-sm text-gray-300 group-hover:text-white transition-colors flex items-center">
              <i className="fas fa-user-secret mr-2 text-accent-pink"></i>Stealth Mode
            </label>
            <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                <input 
                    type="checkbox" 
                    name="stealthMode" 
                    id="stealthMode"
                    checked={config.stealthMode}
                    onChange={handleInputChange}
                    disabled={isLoading || !isConnected}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-dark-400 border-2 border-dark-300 appearance-none cursor-pointer"
                />
                <label 
                    htmlFor="stealthMode"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-dark-700 cursor-pointer border border-dark-400"
                ></label>
            </div>
        </div>
      </div>

      <style jsx>{`
        .range-slider-purple::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          background: #9e3eff;
          border: 2px solid #fff;
          border-radius: 50%;
          cursor: pointer;
          margin-top: -6px; /* Adjust for vertical alignment */
          box-shadow: 0 0 5px rgba(158, 62, 255, 0.7);
        }
        .range-slider-purple::-moz-range-thumb {
          width: 18px;
          height: 18px;
          background: #9e3eff;
          border: 2px solid #fff;
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 0 5px rgba(158, 62, 255, 0.7);
        }
        .toggle-checkbox:checked {
          right: 0;
          border-color: #9e3eff;
          background-color: #9e3eff;
        }
        .toggle-checkbox:checked + .toggle-label {
          background-color: #9e3eff;
           border-color: #9e3eff;
        }
      `}</style>
    </div>
  );
};

export default AiConfiguration;